import type { DuelChoice } from "@ygoevo/world-protocol";
import type { DuelPrompt, PromptKind } from "@ygoevo/protocol";

export type InteractionSurface =
  | "board-command"
  | "decision-modal"
  | "choice-panel"
  | "card-picker"
  | "zone-picker"
  | "turn-order"
  | "declare-panel";

export interface PromptInteractionPolicy {
  surface: InteractionSurface;
  /** `flow` means a non-semantic convenience may answer under explicit, narrow conditions.
   *  `completion` can only finish an already-complete incremental selection with no option left. */
  automatic: "never" | "flow" | "completion";
}

/** The complete human-interaction contract. `satisfies Record<PromptKind, …>` is intentional: adding
 *  a core prompt without classifying its UI surface and automation policy is a compile-time error. */
export const PROMPT_INTERACTIONS = {
  selectIdleCmd: { surface: "board-command", automatic: "never" },
  selectBattleCmd: { surface: "board-command", automatic: "never" },
  selectEffectYN: { surface: "decision-modal", automatic: "never" },
  selectYesNo: { surface: "decision-modal", automatic: "never" },
  selectOption: { surface: "choice-panel", automatic: "never" },
  selectCard: { surface: "card-picker", automatic: "never" },
  selectUnselectCard: { surface: "card-picker", automatic: "completion" },
  selectChain: { surface: "choice-panel", automatic: "flow" },
  selectPlace: { surface: "zone-picker", automatic: "flow" },
  selectPosition: { surface: "decision-modal", automatic: "never" },
  selectTribute: { surface: "card-picker", automatic: "never" },
  selectTp: { surface: "turn-order", automatic: "never" },
  selectSum: { surface: "declare-panel", automatic: "never" },
  selectCounter: { surface: "declare-panel", automatic: "never" },
  announceRace: { surface: "declare-panel", automatic: "never" },
  announceAttrib: { surface: "declare-panel", automatic: "never" },
  announceCard: { surface: "declare-panel", automatic: "never" },
  announceNumber: { surface: "declare-panel", automatic: "never" },
  sortCard: { surface: "declare-panel", automatic: "never" },
  sortChain: { surface: "declare-panel", automatic: "never" },
} as const satisfies Record<PromptKind, PromptInteractionPolicy>;

/** Runtime backstop for system-generated answers. User-configured response skipping is separate;
 *  this only permits choices that carry no strategic information. */
export function isSafeSystemAutoResponse(prompt: DuelPrompt, choice: DuelChoice): boolean {
  switch (prompt.kind) {
    case "selectChain":
      return !prompt.forced && choice.kind === "chain" && choice.index === -1;
    case "selectPlace":
      return choice.kind === "place" && choice.places.length === prompt.count;
    case "selectUnselectCard":
      return prompt.finishable && prompt.selectable.length === 0 && choice.kind === "unselect" && choice.index === null;
    default:
      return false;
  }
}
